import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, Grid, Chip, Divider, List, ListItem, 
  ListItemText, ListItemAvatar, Avatar, TextField, IconButton,
  Container, Tabs, Tab, Stack, Stepper, Step, StepLabel, InputBase
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArticleIcon from '@mui/icons-material/Article';
import CommentIcon from '@mui/icons-material/Comment';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function ContractDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [newDraftFile, setNewDraftFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchContract = async () => {
    try {
      const res = await axios.get(`${API_URL}/contracts/${id}`, { headers });
      setContract(res.data);
    } catch (err) {
      console.error(err);
      alert('Failed to load contract details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContract();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
      try {
          await axios.put(`${API_URL}/contracts/${id}`, { status: newStatus }, { headers });
          fetchContract(); 
      } catch (err) {
          console.error(err);
          alert('Failed to update status');
      }
  };

  const handleAddComment = async () => {
      if (!newComment.trim()) return;
      try {
          await axios.post(`${API_URL}/contracts/${id}/comments`, { comment: newComment }, { headers });
          setNewComment('');
          fetchContract();
      } catch (err) {
          console.error(err);
          alert('Failed to add comment');
      }
  };

  const handleUploadDraft = async () => {
      if (!newDraftFile) return;
      try {
          const formData = new FormData();
          formData.append('contract_pdf', newDraftFile);
          
          await axios.post(`${API_URL}/contracts/${id}/drafts`, formData, {
              headers: { ...headers, 'Content-Type': 'multipart/form-data' }
          });
          setNewDraftFile(null);
          fetchContract();
      } catch (err) {
          console.error(err);
          alert('Failed to upload draft');
      }
  };

  const handleDownload = (draftId, filename) => {
      const encodedFilename = encodeURIComponent(filename);
      const url = `${API_URL}/contracts/${id}/files/${encodedFilename}`;
      window.open(url, '_blank');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      if (files[0].type === 'application/pdf') {
        setNewDraftFile(files[0]);
      } else {
        alert('Please upload a PDF file.');
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) return <Loading />;
  if (!contract) return <Typography>Contract not found</Typography>;

  const steps = [
      { label: 'New', icon: <NoteAddIcon /> },
      { label: 'In Progress', icon: <PendingActionsIcon /> },
      { label: 'Done by Client', icon: <TaskAltIcon /> },
      { label: 'Signed', icon: <VerifiedUserIcon /> }
  ];

  const activeStep = steps.findIndex(s => s.label === contract.status);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ 
        p: 4, 
        mb: 4, 
        borderRadius: 4, 
        background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)', // Blue Gradient like Tickets
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Decorative Circles */}
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
        <Box sx={{ position: 'absolute', bottom: -30, left: 100, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: 1, width: '100%' }}>
                    <Button 
                        startIcon={<ArrowBackIcon />} 
                        onClick={() => navigate('/contracts')} 
                        sx={{ color: 'rgba(255,255,255,0.8)', mb: 2, pl: 0, '&:hover': { color: 'white', bgcolor: 'transparent' } }}
                    >
                        Back to Contracts
                    </Button>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h3" fontWeight="800" sx={{ letterSpacing: -1, fontSize: { xs: '2rem', md: '3rem' } }}>
                            Contract #{contract.id}
                        </Typography>
                        <Chip 
                            label={contract.status} 
                            sx={{ 
                                bgcolor: 'white', 
                                color: 'primary.main', 
                                fontWeight: 'bold',
                                height: 28,
                                boxShadow: 2
                            }} 
                        />
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', opacity: 0.9 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <BusinessIcon fontSize="small" />
                            <Typography variant="subtitle1">{contract.client_name}</Typography>
                        </Box>
                        
                        {contract.project_name && (
                            <>
                                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'white' }} />
                                <Typography variant="subtitle1">Project: {contract.project_name}</Typography>
                            </>
                        )}
                    </Box>
                </Box>

                {/* QR Code Box */}
                <Box sx={{ 
                    bgcolor: 'white', 
                    p: 1.5, 
                    borderRadius: 2, 
                    boxShadow: 3,
                    textAlign: 'center'
                }}>
                    <img 
                        src={`${API_URL}/contracts/${id}/qr`} 
                        alt="QR Code" 
                        style={{ width: 100, height: 100, display: 'block' }} 
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', mt: 0.5, display: 'block', fontWeight: 'bold' }}>
                        SCAN ME
                    </Typography>
                </Box>
            </Box>
        </Box>
      </Paper>

      {/* Main Content Paper */}
      <Paper sx={{ mb: 3, p: 3, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }} elevation={0}>
        
        {/* Contract Details Section */}
        <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <ArticleIcon color="primary" fontSize="large" />
                <Typography variant="h5" fontWeight="bold">Contract Details</Typography>
            </Box>
            <Grid container spacing={4}>
                {/* Left Column: Description & Info */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>Description</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary', lineHeight: 1.6 }}>
                            {contract.description || "No description provided."}
                        </Typography>

                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ mb: 2 }}>Workflow Progress</Typography>
                            <Stepper activeStep={activeStep} alternativeLabel>
                                {steps.map((step, index) => (
                                    <Step key={step.label}>
                                        <StepLabel StepIconComponent={() => (
                                            <Avatar 
                                                onClick={() => {
                                                    // Optional: Allow clicking to change status if logic permits
                                                    if (index > activeStep && index === activeStep + 1) handleStatusChange(step.label);
                                                }}
                                                sx={{ 
                                                    cursor: index === activeStep + 1 ? 'pointer' : 'default',
                                                    bgcolor: activeStep >= index ? 'success.main' : 'grey.300',
                                                    color: 'white',
                                                    width: 40, height: 40
                                                }}
                                            >
                                                {step.icon}
                                            </Avatar>
                                        )}>
                                            {step.label}
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            {activeStep < steps.length - 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <Button 
                                        variant="contained" 
                                        onClick={() => handleStatusChange(steps[activeStep + 1].label)}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        Mark as {steps[activeStep + 1].label}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Grid>

                {/* Right Column: Meta Data */}
                <Grid item xs={12} md={4}>
                    <Stack spacing={4} sx={{ height: '100%' }}>
                        <Box>
                            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}>Location & DDP</Typography>
                            <Stack spacing={2}>
                                <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <LocationOnIcon color="action" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Location</Typography>
                                        <Typography variant="body2" fontWeight="bold">{contract.country}</Typography>
                                        <Typography variant="body2">{contract.division}</Typography>
                                    </Box>
                                </Paper>
                                <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <PendingActionsIcon color="action" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">DDP</Typography>
                                        <Typography variant="body2" fontWeight="bold">{contract.ddp}</Typography>
                                    </Box>
                                </Paper>
                            </Stack>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}>Ownership</Typography>
                            <Stack spacing={2}>
                                <TextField 
                                    fullWidth 
                                    size="small"
                                    label="Created By" 
                                    value={contract.created_by} 
                                    InputProps={{ readOnly: true }}
                                />
                                <TextField 
                                    fullWidth 
                                    size="small"
                                    label="Client" 
                                    value={contract.client_name}
                                    InputProps={{ readOnly: true }}
                                />
                            </Stack>
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Grid container spacing={4}>
            {/* Draft Versions Section - Left Side */}
            <Grid item xs={12} md={6}>
                <Box 
                    sx={{ 
                        mb: 5, 
                        height: '100%',
                        display: 'flex', 
                        flexDirection: 'column'
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon color="primary" fontSize="large" />
                            <Typography variant="h5" fontWeight="bold">Contract Versions</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
                            <Button variant="outlined" component="label" startIcon={<UploadIcon />} sx={{ flexGrow: { xs: 1, sm: 0 }, whiteSpace: 'nowrap' }}>
                                {newDraftFile ? newDraftFile.name : "Select PDF"}
                                <input type="file" hidden accept="application/pdf" onChange={e => setNewDraftFile(e.target.files[0])} />
                            </Button>
                            <Button variant="contained" disabled={!newDraftFile} onClick={handleUploadDraft} sx={{ flexGrow: { xs: 1, sm: 0 } }}>
                                Upload
                            </Button>
                        </Box>
                    </Box>
                    
                    {/* Drag and Drop Zone */}
                    <Box 
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        sx={{ 
                            flexGrow: 1,
                            minHeight: 200,
                            border: '2px dashed', 
                            borderColor: isDragging ? 'primary.main' : 'divider',
                            borderRadius: 2,
                            bgcolor: isDragging ? 'action.hover' : 'transparent',
                            p: 2,
                            transition: 'all 0.2s'
                        }}
                    >
                        <Grid container spacing={2}>
                            {contract.drafts && contract.drafts.length > 0 ? (
                                contract.drafts.map((draft) => (
                                    <Grid item xs={12} key={draft.id}>
                                        <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 2, bgcolor: 'white' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ bgcolor: 'primary.light' }}><DescriptionIcon /></Avatar>
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="bold">Version {draft.version}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Uploaded by {draft.created_by} on {new Date(draft.created_at).toLocaleString()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Button startIcon={<DownloadIcon />} onClick={() => handleDownload(draft.id, draft.file_path)}>
                                                Download
                                            </Button>
                                        </Paper>
                                    </Grid>
                                ))
                            ) : (
                                <Grid item xs={12}>
                                    <Box sx={{ p: 4, textAlign: 'center', mt: 4 }}>
                                        <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                        <Typography color="textSecondary">Drag and drop PDF files here</Typography>
                                        <Typography variant="caption" color="textSecondary">or use the Select PDF button above</Typography>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Box>
            </Grid>

            {/* Comments Section - Right Side */}
            <Grid item xs={12} md={6}>
                 <Box sx={{ height: 600, borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' }, pl: { md: 4 }, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CommentIcon color="primary" fontSize="large" />
                        <Typography variant="h5" fontWeight="bold">Discussion</Typography>
                    </Box>
                    
                    {/* Chat Area */}
                    <Paper variant="outlined" sx={{ flexGrow: 1, mb: 2, p: 2, bgcolor: '#f5f7fa', overflow: 'auto', borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                         {contract.comments && contract.comments.length > 0 ? (
                            contract.comments.map((comment) => (
                                <Box key={comment.id} sx={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: utilHashColor(comment.user) }}>
                                            {comment.user.charAt(0)}
                                        </Avatar>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                            {comment.user}
                                        </Typography>
                                        <Typography variant="caption" color="text.disabled">
                                            {new Date(comment.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                        </Typography>
                                    </Box>
                                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'white', borderRadius: '4px 16px 16px 16px', border: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="body2">{comment.comment}</Typography>
                                    </Paper>
                                </Box>
                            ))
                        ) : (
                            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="body2" color="text.secondary">No comments yet. Start the discussion!</Typography>
                            </Box>
                        )}
                    </Paper>

                    {/* Chat Input */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Type a message..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 4 } }}
                        />
                        <IconButton 
                            color="primary" 
                            onClick={handleAddComment} 
                            disabled={!newComment.trim()} 
                            sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, width: 40, height: 40 }}
                        >
                            <SendIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            </Grid>
        </Grid>

      </Paper>
    </Container>
  );
}

// Simple hash for avatar colors
const utilHashColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
    return '#' + '00000'.substring(0, 6 - c.length) + c;
}

export default ContractDetails;
